import { ENTITY_ID } from '../../domain/domainCodes.js'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { BookOpen, Check, ChevronDown, ChevronUp, Search } from 'lucide-react'
import DatePicker from '../../components/DatePicker'
import FileField from '../../components/FileField'
import FormPage from '../../components/FormPage'
import useGoBack from '../../components/useGoBack'
import { FormCols, FormSection, InfoNote, SwitchField, Textarea } from '../../components/Form'
import FormRow from '../../components/FormRow'
import IconButton from '../../components/IconButton'
import Select from '../../components/Select'
import TextField from '../../components/TextField'
import { useToast } from '../../components/toastContext'
import { getCourseForEdit, getProgramsPage, saveCourse } from './programsService'
import { strings as S } from './strings'
import './QuickProgramPage.css'
import './CourseFormPage.css'
import { serverText } from '../../lib/i18n'

const EMPTY = {
  arabicName: '',
  arabicDescription: '',
  englishName: '',
  englishDescription: '',
  level: '1',
  startDate: '',
  endDate: '',
  contentType: 'MP4',
  hasExam: 'true',
  banner: null,
  scorm: null,
  topicIds: [],

  passLimit: 50,
  examTries: 3,
  questionsCount: 5,
  rewatchPercentage: 100,
  forceWatchContent: false,
  hideAnswers: false,
  preventIfSuccess: false,
  quizEnable: false,
}

/**
 * دورة تدريبية — _ProgramManagement_Courses_Modal.
 *
 * The last of the legacy's five wizards, and the button that opened it did
 * nothing at all: the programme detail screen rendered `دورة تدريبية جديدة`
 * and had no handler behind it.
 *
 *   1  the course   both names and descriptions, level, period, content type,
 *                   whether it carries an exam, and its banner
 *   2  the content  a SCORM package, or the topics the course is made of —
 *                   which of the two depends on the content type chosen above
 *   3  the exam     pass limit, tries, question count, and the four rules that
 *                   decide what a failed attempt costs
 *
 * Two of the legacy's own conditionals are honoured rather than hidden in
 * script: SCORM has no topics to pick, and a course with no exam has no exam
 * settings — so neither step asks for what it cannot use.
 *
 * The legacy sorts its topic list by dragging a handle. Here the chosen topics
 * stand in their own panel beside the grid, in order, moved with two arrows —
 * the same `data-order` the drag rewrites, but visible without hovering and
 * usable from a keyboard.
 */
export default function CourseFormPage() {
  const { id: programId, courseId } = useParams()
  const isEdit = Number(courseId) > 0
  const navigate = useNavigate()
  /* Back to the page this form was opened from — the list as it was left,
     not its first page. See components/useGoBack.js. */
  const goBack = useGoBack(`/programs/${programId}`)
  const toast = useToast()
  const [values, setValues] = useState(EMPTY)
  const [errors, setErrors] = useState({})
  const [topics, setTopics] = useState([])
  const [levels, setLevels] = useState([])
  const [step, setStep] = useState(0)
  const [query, setQuery] = useState('')
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let alive = true
    /* The topics come from the programmes payload now, alongside the levels — one call
       instead of a mock import and a call. */
    /* With the programme id: the handler only fills the level lists when it is given one. */
    getProgramsPage(programId)
      .then((page) => {
        if (!alive) return
        setTopics(page.categories)
        setLevels(page.levels)
      })
      .catch(() => {
        /* Empty selects rather than a half-loaded form; the save will say why if it is
           attempted without them. */
        if (!alive) return
        setTopics([])
        setLevels([])
      })
    return () => {
      alive = false
    }
  }, [programId])

  /* EDITING LOADS BOTH the course and its exam — see `getCourseForEdit`. Without the exam
     half, saving would post this form's defaults over the pass mark, the attempt count and
     the three switches, because `SaveCourse` rewrites them from whatever it is given. */
  useEffect(() => {
    if (!isEdit) return undefined

    let alive = true
    setLoading(true)
    getCourseForEdit(programId, courseId)
      .then((course) => {
        if (!alive) return
        if (course) setValues((previous) => ({ ...previous, ...course }))
        setLoading(false)
      })
      .catch(() => {
        if (!alive) return
        setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [isEdit, programId, courseId])

  const set = (key) => (value) => {
    setValues((previous) => ({ ...previous, [key]: value }))
    setErrors((previous) => ({ ...previous, [key]: undefined }))
  }

  const isScorm = values.contentType === 'SCORM'
  const hasExam = values.hasExam === 'true'
  /* A course that ALREADY HAS AN EXAM is not asked for a question count. The count exists to
     generate the exam's question rules, and `SaveCourse` only generates them when it is
     making the exam; on an existing one it rewrites the pass mark, the attempt count and the
     three switches and leaves the rules alone. The legacy hides the field for the same
     reason and offers its rules editor in its place — which is the exam button on the
     programme's own table here. */
  const hasStoredExam = isEdit && Number(values.examId) > 0

  /* Step two is whatever the content type made it, so the rail names the thing
     the step actually asks for rather than promising topics a SCORM course has
     none of. */
  const steps = useMemo(
    () =>
      [
        { id: 'info', label: S.course.steps.info },
        { id: 'content', label: isScorm ? S.course.scorm : S.course.steps.topics },
        hasExam && { id: 'exam', label: S.course.steps.exam },
      ].filter(Boolean),
    [hasExam, isScorm],
  )

  /* The legacy's Publicity select and its search box, over the same list. */
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase()
    /* `value`/`label` — THE OPTION SHAPE `getProgramsPage` returns, which is what the quick
       wizard beside this one reads. This screen still read `id`/`name`/`publicity` from an
       earlier source, so every topic rendered nameless, none could match the search, and a
       course's own topics never showed as chosen when its form was opened to be edited. */
    return topics.filter((topic) => !needle || String(topic.label).toLowerCase().includes(needle))
  }, [topics, query])

  /* `topicIds` is the order, so the chosen panel reads straight off it. */
  const chosen = useMemo(
    () =>
      values.topicIds
        .map((topicId) => topics.find((topic) => topic.value === topicId))
        .filter(Boolean),
    [values.topicIds, topics],
  )

  const move = (index, by) =>
    setValues((previous) => {
      const next = [...previous.topicIds]
      const to = index + by
      if (to < 0 || to >= next.length) return previous
      ;[next[index], next[to]] = [next[to], next[index]]
      return { ...previous, topicIds: next }
    })

  const validateStep = (index) => {
    const next = {}
    if (index === 0) {
      for (const key of ['arabicName', 'englishName', 'arabicDescription', 'englishDescription']) {
        if (!values[key].trim()) next[key] = S.form.required
      }
      if (!values.startDate) next.startDate = S.form.required
      if (!values.endDate) next.endDate = S.form.required
      else if (values.startDate && values.endDate <= values.startDate)
        next.endDate = S.form.dateOrder
    }
    if (index === 1 && !isScorm && values.topicIds.length === 0)
      next.topicIds = S.course.topicsRequired
    if (index === 2) {
      if (!(values.passLimit >= 1 && values.passLimit <= 100)) next.passLimit = S.form.range
      if (!hasStoredExam && !(values.questionsCount >= 1 && values.questionsCount <= 200))
        next.questionsCount = S.form.range
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const submit = async () => {
    setSaving(true)
    const outcome = await saveCourse({ ...values, id: isEdit ? Number(courseId) : ENTITY_ID.NEW, programId })
    if (!outcome.ok) {
      setSaving(false)
      return toast.error(outcome.message || S.actionFailed)
    }
    toast.success(serverText(outcome.message) || S.form.saved)
    navigate(`/programs/${programId}`)
  }

  const toggleTopic = (topicId) =>
    setValues((previous) => ({
      ...previous,
      topicIds: previous.topicIds.includes(topicId)
        ? previous.topicIds.filter((value) => value !== topicId)
        : [...previous.topicIds, topicId],
    }))

  return (
    <FormPage
      icon={BookOpen}
      title={isEdit ? S.course.editTitle : S.course.title}
      subtitle={S.title}
      steps={steps}
      step={step}
      onStep={setStep}
      validateStep={validateStep}
      onBack={goBack}
      onSubmit={submit}
      submitLabel={S.form.save}
      submitting={saving || loading}
      aside={
        step === 1 && !isScorm ? (
          <div className="crsform__order">
            <p className="crsform__order-title">
              <bdi>{S.course.order}</bdi>
            </p>
            <p className="crsform__order-hint">
              <bdi>{S.course.orderHint}</bdi>
            </p>

            {chosen.length === 0 ? (
              <p className="crsform__order-empty">
                <bdi>{S.course.orderEmpty}</bdi>
              </p>
            ) : (
              <ol className="crsform__order-list">
                {chosen.map((topic, index) => (
                  <li key={topic.value} className="crsform__order-row">
                    <span className="crsform__order-index tabular">{index + 1}</span>
                    <bdi className="crsform__order-name">{topic.label}</bdi>
                    <span className="crsform__order-moves">
                      <IconButton
                        icon={ChevronUp}
                        label={S.course.moveUp}
                        size="sm"
                        disabled={index === 0}
                        onClick={() => move(index, -1)}
                      />
                      <IconButton
                        icon={ChevronDown}
                        label={S.course.moveDown}
                        size="sm"
                        disabled={index === chosen.length - 1}
                        onClick={() => move(index, 1)}
                      />
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        ) : null
      }
    >
      {({ step: current }) => {
        if (current === 0) {
          return (
            <FormSection>
              <FormCols>
                <FormRow label={S.form.arabicName} error={errors.arabicName}>
                  {(fid) => (
                    <TextField
                      id={fid}
                      value={values.arabicName}
                      onChange={set('arabicName')}
                      invalid={!!errors.arabicName}
                      autoComplete="off"
                    />
                  )}
                </FormRow>
                <FormRow label={S.form.englishName} error={errors.englishName}>
                  {(fid) => (
                    <TextField
                      id={fid}
                      dir="ltr"
                      value={values.englishName}
                      onChange={set('englishName')}
                      invalid={!!errors.englishName}
                      autoComplete="off"
                    />
                  )}
                </FormRow>
                <FormRow label={S.form.arabicDescription} error={errors.arabicDescription}>
                  {(fid) => (
                    <Textarea
                      id={fid}
                      rows={4}
                      value={values.arabicDescription}
                      onChange={set('arabicDescription')}
                      invalid={!!errors.arabicDescription}
                    />
                  )}
                </FormRow>
                <FormRow label={S.form.englishDescription} error={errors.englishDescription}>
                  {(fid) => (
                    <Textarea
                      id={fid}
                      dir="ltr"
                      rows={4}
                      value={values.englishDescription}
                      onChange={set('englishDescription')}
                      invalid={!!errors.englishDescription}
                    />
                  )}
                </FormRow>
                <FormRow label={S.course.level}>
                  {(fid) => (
                    <Select
                      id={fid}
                      value={values.level}
                      onChange={set('level')}
                      /* `value`/`label` again — the same option shape the topics arrive in,
                         which is why this select opened empty on a course that has a level. */
                      options={levels.map((row) => ({ value: String(row.value), label: row.label }))}
                    />
                  )}
                </FormRow>
                <FormRow label={S.course.startDate} error={errors.startDate}>
                  {(fid) => (
                    <DatePicker
                      id={fid}
                      value={values.startDate}
                      onChange={set('startDate')}
                      invalid={!!errors.startDate}
                    />
                  )}
                </FormRow>
                <FormRow label={S.course.endDate} error={errors.endDate}>
                  {(fid) => (
                    <DatePicker
                      id={fid}
                      value={values.endDate}
                      onChange={set('endDate')}
                      invalid={!!errors.endDate}
                    />
                  )}
                </FormRow>
                {/* LOCKED ONCE THE COURSE EXISTS. `SaveCourse` refuses the change outright
                    — "لا يمكن تغيير نوع محتوى الدورة التدريبية" — because the content that is
                    already uploaded is of one kind and the learners' progress is recorded
                    against it. Offering the choice would only produce a rejected save. */}
                <FormRow label={S.course.contentType} hint={isEdit ? S.course.contentTypeLocked : undefined}>
                  {(fid) => (
                    <Select
                      id={fid}
                      value={values.contentType}
                      onChange={set('contentType')}
                      disabled={isEdit}
                      options={[
                        { value: 'MP4', label: 'MP4' },
                        { value: 'SCORM', label: 'SCORM' },
                      ]}
                    />
                  )}
                </FormRow>
                <FormRow label={S.course.hasExam}>
                  {(fid) => (
                    <Select
                      id={fid}
                      value={values.hasExam}
                      onChange={set('hasExam')}
                      options={[
                        { value: 'true', label: S.course.yes },
                        { value: 'false', label: S.course.no },
                      ]}
                    />
                  )}
                </FormRow>
                <FormRow label={S.course.banner}>
                  {(fid) => (
                    <FileField id={fid} accept="image/png" onChange={set('banner')} />
                  )}
                </FormRow>
              </FormCols>
            </FormSection>
          )
        }

        if (current === 1) {
          /* SCORM carries its own content, so there is nothing to pick — the
             legacy swaps the same step's body on the content type. */
          if (isScorm) {
            return (
              <FormSection>
                <FormCols>
                  <FormRow label={S.course.file} hint={S.course.scormHint}>
                    {(fid) => <FileField id={fid} accept=".zip" onChange={set('scorm')} />}
                  </FormRow>
                </FormCols>
              </FormSection>
            )
          }

          return (
            <FormSection>
              <InfoNote>
                <bdi>{S.course.topicsNote}</bdi>
              </InfoNote>

              {/* A search over the list — twenty-eight topics is too many to scan for one.

                  The legacy pairs it with a Publicity select. `CategoryDto` carries no
                  publicity, so that filter matched nothing and emptied the list whichever
                  way it was set; it is recorded in COSMETIC_UI rather than shipped broken. */}
              <div className="crsform__filters">
                <label className="crsform__search">
                  <Search size={15} strokeWidth={1.75} aria-hidden="true" />
                  <input
                    type="search"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder={S.course.searchTopics}
                    aria-label={S.course.searchTopics}
                  />
                </label>
              </div>

              {errors.topicIds && (
                <p className="quickprog__error" role="alert">
                  <bdi>{errors.topicIds}</bdi>
                </p>
              )}

              <ul className="quickprog__topics">
                {shown.map((topic) => {
                  const on = values.topicIds.includes(topic.value)
                  return (
                    <li key={topic.value}>
                      <button
                        type="button"
                        className={`quickprog__topic${on ? ' is-on' : ''}`}
                        onClick={() => toggleTopic(topic.value)}
                        aria-pressed={on}
                      >
                        <span className="quickprog__tick" aria-hidden="true">
                          {on && <Check size={13} strokeWidth={3} />}
                        </span>
                        <bdi className="quickprog__topic-name">{topic.label}</bdi>
                      </button>
                    </li>
                  )
                })}
              </ul>
            </FormSection>
          )
        }

        return (
          <FormSection>
            <FormCols>
              <FormRow label={S.course.passLimit} error={errors.passLimit}>
                {(fid) => (
                  <TextField
                    id={fid}
                    type="number"
                    min={1}
                    max={100}
                    value={values.passLimit}
                    onChange={(value) => set('passLimit')(Number(value))}
                    invalid={!!errors.passLimit}
                  />
                )}
              </FormRow>
              <FormRow label={S.course.examTries}>
                {(fid) => (
                  <TextField
                    id={fid}
                    type="number"
                    min={1}
                    value={values.examTries}
                    onChange={(value) => set('examTries')(Number(value))}
                  />
                )}
              </FormRow>
              {!hasStoredExam && (
                <FormRow label={S.course.questionsCount} error={errors.questionsCount}>
                  {(fid) => (
                    <TextField
                      id={fid}
                      type="number"
                      min={1}
                      max={200}
                      value={values.questionsCount}
                      onChange={(value) => set('questionsCount')(Number(value))}
                      invalid={!!errors.questionsCount}
                    />
                  )}
                </FormRow>
              )}
              {/* The legacy only lets this be edited while the rewatch rule is
                  on, which is what `setPercentageEditable()` does. Its tooltip
                  becomes the field's own hint — the rule it states is the only
                  thing that makes the number valid. */}
              {values.forceWatchContent && (
                <FormRow label={S.course.rewatchPercentage} hint={S.course.rewatchHint}>
                  {(fid) => (
                    <TextField
                      id={fid}
                      type="number"
                      min={1}
                      max={100}
                      value={values.rewatchPercentage}
                      onChange={(value) => set('rewatchPercentage')(Number(value))}
                    />
                  )}
                </FormRow>
              )}
            </FormCols>

            <SwitchField
              checked={values.forceWatchContent}
              onChange={set('forceWatchContent')}
              label={S.course.forceWatch}
            />
            <SwitchField
              checked={values.hideAnswers}
              onChange={set('hideAnswers')}
              label={S.course.hideAnswers}
            />
            <SwitchField
              checked={values.preventIfSuccess}
              onChange={set('preventIfSuccess')}
              label={S.course.preventIfSuccess}
            />
            <SwitchField
              checked={values.quizEnable}
              onChange={set('quizEnable')}
              label={S.course.quizEnable}
            />
          </FormSection>
        )
      }}
    </FormPage>
  )
}
